import type { POI, TravelMode } from "@poi-planner/shared";
import { hasUsableCoordinates } from "./dayMapPois";

/** Prefer the POI's canonical Maps URL, with coordinates as a safe fallback. */
export function poiMapsUrl(poi: POI): string | null {
  if (poi.googleMapsUrl?.trim()) return poi.googleMapsUrl.trim();
  if (!hasUsableCoordinates(poi)) return null;
  const query = encodeURIComponent(`${poi.lat},${poi.lng}`);
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

/**
 * Maps URL for a basemap point of interest.
 *
 * Google's documented `?api=1&query=` search takes free text OR coordinates,
 * never both: passing "<name> <lat>,<lng>" is searched literally, fails to
 * match, and drops the viewer at their own IP location instead of the place.
 * The path form with an `@lat,lng,zoom` viewport searches the name anchored to
 * the right map area, which is also the form Google's own redirects use.
 * Coordinates alone are the fallback when there is no usable name.
 */
const BASEMAP_PLACE_MAPS_ZOOM = 17;
const BASEMAP_PLACE_COORD_PRECISION = 6;

export function basemapPlaceMapsUrl(place: {
  name: string;
  lat: number;
  lng: number;
}): string {
  const lat = place.lat.toFixed(BASEMAP_PLACE_COORD_PRECISION);
  const lng = place.lng.toFixed(BASEMAP_PLACE_COORD_PRECISION);
  const name = place.name.trim();

  if (!name) {
    const query = encodeURIComponent(`${lat},${lng}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  }

  return `https://www.google.com/maps/search/${encodeURIComponent(
    name,
  )}/@${lat},${lng},${BASEMAP_PLACE_MAPS_ZOOM}z`;
}

const GOOGLE_DIRECTIONS_TRAVEL_MODE: Record<TravelMode, string> = {
  walk: "walking",
  transit: "transit",
  drive: "driving",
};

/**
 * Google Maps directions between two points. Coordinates rather than names:
 * stored POIs have no place id, and a name search can resolve to a different
 * branch of the same chain.
 */
export function directionsMapsUrl(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
  mode: TravelMode,
): string {
  const point = ({ lat, lng }: { lat: number; lng: number }) =>
    `${lat.toFixed(BASEMAP_PLACE_COORD_PRECISION)},${lng.toFixed(BASEMAP_PLACE_COORD_PRECISION)}`;
  const params = new URLSearchParams({
    api: "1",
    origin: point(from),
    destination: point(to),
    travelmode: GOOGLE_DIRECTIONS_TRAVEL_MODE[mode],
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}
